<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query'
import { useField, useForm } from 'vee-validate'
import { nextTick, ref, watch } from 'vue'
import * as yup from 'yup'
import { type CreateCommissionPayload, createCommissionAction } from '@/api/functions/settings/get-commissions-action'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'

const props = defineProps({
  modelValue: Boolean,
  currencies: { type: Array, default: () => [] },
})

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel'])

const toast = useToast()
// const queryClient = useQueryClient();

// Validation schema
const schema = yup.object({
  currency_id: yup.string().required('انتخاب ارز الزامی است'),
  type: yup.string().required('نوع کارمزد الزامی است'),
  from: yup.number().required('مقدار شروع الزامی است').typeError('باید عدد باشد'),
  till: yup.number().required('مقدار پایان الزامی است').typeError('باید عدد باشد'),
  rate: yup
    .number()
    .required('مقدار کارمزد الزامی است')
    .typeError('باید عدد باشد')
    .min(0, 'مقدار کارمزد نمی‌تواند منفی باشد')
    .max(999.99, 'مقدار کارمزد نمی‌تواند بیشتر از 999.99 باشد')
    .test('max-two-decimals', 'حداکثر دو رقم اعشار مجاز است', (value) => {
      if (value === undefined || value === null) return false
      const decimalPart = value.toString().split('.')[1]
      return !decimalPart || decimalPart.length <= 2
    }),
})

// Form setup
const { handleSubmit, errors, resetForm } = useForm({
  validationSchema: schema,
  initialValues: {
    currency_id: '',
    type: '',
    from: null,
    till: null,
    rate: null,
  },
})

// Fields
const { value: currency_id } = useField('currency_id')
const { value: type } = useField('type')
const { value: from } = useField('from')
const { value: till } = useField('till')
const { value: rate } = useField('rate')
const toEnDigits = (s: string) =>
  s.replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d))).replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))

const formatRate = (raw: unknown) => {
  const s = toEnDigits(String(raw ?? '')).replace(/[^\d.]/g, '')
  const [intPart = '', ...rest] = s.split('.')
  if (!intPart && !rest.length) return ''
  const int = intPart.slice(0, 3)
  const dec = rest.join('').slice(0, 2)
  if (!int) return `0.${dec}`
  return rest.length ? `${int}.${dec}` : int
}

const rateText = ref('')

const onRateInput = async (raw: string) => {
  rateText.value = raw
  await nextTick()
  rateText.value = formatRate(raw)
  rate.value = rateText.value === '' ? null : rateText.value
}
watch(
  rate,
  (v) => {
    const next = v == null ? '' : String(v)
    if (rateText.value !== next) rateText.value = next
  },
  { immediate: true }
)

// Mutation
const createCommissionMutation = useMutationApi({
  mutationFn: (payload: CreateCommissionPayload) => createCommissionAction(payload),
  onSuccess: () => {
    toast.success('کارمزد جدید با موفقیت ایجاد شد', 'موفقیت')
    emit('confirm')
    closeModal()
    // queryClient.invalidateQueries({ queryKey: ['commissions'] });
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

// Submit
const onSubmit = handleSubmit((values) => {
  createCommissionMutation.mutate({
    ...values,
    rate: values.rate == null ? null : Number(values.rate),
  } as unknown as CreateCommissionPayload)
})

const closeModal = () => {
  emit('update:modelValue', false)
  emit('cancel')
  resetForm()
}
</script>

<template>
    <Modal :model-value="props.modelValue" @update:model-value="$emit('update:modelValue', $event)" width="600px"
        background="#FFF" :closable="true" title="ایجاد کارمزد" closeDirection="left" class="update-request">

        <form @submit.prevent="onSubmit" class="flex flex-col min-h-[430px]">
            <div class="flex-1 overflow-auto">
                <div class="grid px-4 pt-6 pb-9 gap-2">
                    <Select v-model="currency_id" label="ارز" :options="currencies" :error="errors.currency_id" />
                    <Select v-model="type" label="نوع کارمزد" :options="[
                        { label: 'درصد', value: 'PERCENTAGE' },
                        { label: 'مقدار ثابت', value: 'FIXED' }
                    ]" :error="errors.type" />
                    <Input v-model="from" name="from" label="از مبلغ" type="number" :error="errors.from" />
                    <Input v-model="till" name="till" label="تا مبلغ" type="number" :error="errors.till" />
                    <Input :model-value="rateText" @update:model-value="onRateInput" name="rate" label="مقدار کارمزد"
                        type="text" inputmode="decimal" :error="errors.rate"  />
                </div>
            </div>

            <div class="sticky bottom-0 bg-white z-50 flex justify-between gap-6 w-full pt-3 pb-9 px-2 border-gray-200">
                <Button type="submit" label="ایجاد کارمزد" :isAdmin="true" class="w-9/12 h-12 rounded-xl"
                    :disabled="createCommissionMutation.isPending.value"
                    :loading="createCommissionMutation.isPending.value" />
                <Button type="button" @click="closeModal" variant="outline" label="بازگشت" :isAdmin="true"
                    class="w-4/12 h-12 rounded-xl border-2" :disabled="createCommissionMutation.isPending.value" />
            </div>
        </form>
    </Modal>
</template>
